import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Download, Pencil, Trash2, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { PageHeader, Panel, Badge, Select, fmtDT } from "@/components/admin/ui";

export default function InventoryCounts() {
  const [stores, setStores] = useState([]);
  const [counts, setCounts] = useState([]);
  const [storeId, setStoreId] = useState("");
  const [open, setOpen] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState([]);

  useEffect(() => { api.get("/stores").then((r) => setStores(r.data)); }, []);
  const load = () => api.get(`/inventory/counts${storeId ? `?store_id=${storeId}` : ""}`).then((r) => setCounts(r.data));
  useEffect(() => { load(); }, [storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const startEdit = (c) => { setEditing(c.id); setDraft(c.items.map((i) => ({ ...i }))); setOpen(c.id); };
  const saveEdit = async (c) => {
    try { await api.put(`/inventory/counts/${c.id}`, { items: draft.map((i) => ({ ...i, count: parseFloat(i.count) || 0 })) }); toast.success("Count updated"); setEditing(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.store_name} count from ${c.date}?`)) return;
    try { await api.delete(`/inventory/counts/${c.id}`); toast.success("Count deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const byDate = useMemo(() => {
    const m = {};
    counts.forEach((c) => { (m[c.date] = m[c.date] || []).push(c); });
    return Object.entries(m).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [counts]);

  const today = new Date().toISOString().slice(0, 10);
  const countedToday = new Set(counts.filter((c) => c.date === today).map((c) => c.store_id));

  return (
    <div className="p-6 lg:p-10">
      <PageHeader eyebrow="Company" title="Inventory Counts" testId="inventory-counts-title"
        subtitle={`${countedToday.size} of ${stores.length} stores counted today`}
        actions={<Select value={storeId} onChange={(e) => setStoreId(e.target.value)} data-testid="counts-store-filter"><option value="">All stores</option>{stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</Select>}
      />
      {!storeId && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
          {stores.map((s) => (
            <Link key={s.id} to={`/admin/stores/${s.id}/inventory`} className={`rounded-xl border p-4 text-sm ${countedToday.has(s.id) ? "bg-emerald-50 border-emerald-200" : "bg-white border-neutral-200"}`} data-testid={`count-store-${s.id}`}>
              <div className="font-medium">{s.name}</div>
              <div className={`text-xs mt-1 ${countedToday.has(s.id) ? "text-emerald-700" : "text-neutral-400"}`}>{countedToday.has(s.id) ? "Counted today" : "Not counted today"}</div>
            </Link>
          ))}
        </div>
      )}
      {byDate.length === 0 && <Panel className="p-10 text-center text-sm text-neutral-500" data-testid="counts-empty">No inventory counts yet.</Panel>}
      {byDate.map(([date, rows]) => (
        <div key={date} className="mb-6">
          <h2 className="font-display font-semibold mb-2">{date}</h2>
          <Panel className="divide-y divide-neutral-100">
            {rows.map((c) => (
              <div key={c.id} className="p-4" data-testid={`count-${c.id}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-medium">{c.store_name} <span className="text-neutral-400 font-normal">· {c.items.length} items</span> {c.is_late && <Badge tone="orange">late</Badge>}</div>
                    <div className="text-xs text-neutral-500">Submitted {fmtDT(c.submitted_at)} by <b>{c.submitted_by_name}</b></div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setOpen(open === c.id ? null : c.id)} data-testid={`count-view-${c.id}`}>{open === c.id ? "Hide" : "View"}</Button>
                    {editing === c.id
                      ? <><Button size="sm" onClick={() => saveEdit(c)} className="bg-emerald-600" data-testid={`count-save-${c.id}`}><Save className="w-3 h-3 mr-1"/>Save</Button><Button size="sm" variant="outline" onClick={() => setEditing(null)}><X className="w-3 h-3"/></Button></>
                      : <Button size="sm" variant="outline" onClick={() => startEdit(c)} data-testid={`count-edit-${c.id}`}><Pencil className="w-3 h-3"/></Button>}
                    <a href={`${api.defaults.baseURL}/inventory/counts/${c.id}/pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Download className="w-3 h-3"/></Button></a>
                    <Button size="sm" variant="outline" onClick={() => remove(c)} className="text-red-600" data-testid={`count-delete-${c.id}`}><Trash2 className="w-3 h-3"/></Button>
                  </div>
                </div>
                {open === c.id && editing !== c.id && (
                  <table className="w-full text-xs mt-3"><tbody className="divide-y divide-neutral-100">
                    {c.items.map((it, i) => <tr key={i}><td className="py-1.5 font-medium">{it.name}</td><td className="py-1.5 text-right">{it.count} {it.unit}</td><td className="py-1.5 pl-4 text-neutral-500">{it.notes}</td></tr>)}
                  </tbody></table>
                )}
                {editing === c.id && (
                  <table className="w-full text-xs mt-3" data-testid={`count-edit-table-${c.id}`}><tbody className="divide-y divide-neutral-100">
                    {draft.map((it, i) => (
                      <tr key={i}><td className="py-1.5 font-medium w-1/3">{it.name} <span className="text-neutral-400">{it.unit}</span></td>
                        <td className="py-1.5 w-28"><Input type="number" step="any" value={it.count} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, count: e.target.value } : x)))} className="h-8" data-testid={`count-edit-qty-${i}`}/></td>
                        <td className="py-1.5 pl-3"><Input value={it.notes || ""} onChange={(e) => setDraft(draft.map((x, j) => (j === i ? { ...x, notes: e.target.value } : x)))} className="h-8" placeholder="Notes"/></td></tr>
                    ))}
                  </tbody></table>
                )}
              </div>
            ))}
          </Panel>
        </div>
      ))}
    </div>
  );
}
